import { useState, useEffect } from 'react';
import { Bot, BarChart3, Settings, Phone, PhoneOutgoing, Clock, MessageCircle, Mail, ScrollText, Lock } from '../icons/wp-icons';
import { Navigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import KiUebersicht from '../components/KiZentrale/KiUebersicht';
import KiEinstellungen from '../components/KiZentrale/KiEinstellungen';
import TelefonAgent from '../components/KiZentrale/TelefonAgent';
import WhatsAppAgent from '../components/KiZentrale/WhatsAppAgent';
import EmailAgent from '../components/KiZentrale/EmailAgent';
import KiProtokoll from '../components/KiZentrale/KiProtokoll';
import { usePermissions, MODULES } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import { kiZentraleApi } from '../api/kiZentraleApi';

const ALL_TABS = [
  { id: 'uebersicht', label: 'Übersicht', shortLabel: 'Übersicht', icon: BarChart3 },
  { id: 'einstellungen', label: 'Einstellungen', shortLabel: 'Einstell.', icon: Settings },
  { id: 'telefon', label: 'Telefon', shortLabel: 'Telefon', icon: Phone },
  { id: 'whatsapp', label: 'WhatsApp', shortLabel: 'WhatsApp', icon: MessageCircle },
  { id: 'email', label: 'E-Mail', shortLabel: 'E-Mail', icon: Mail },
  { id: 'protokoll', label: 'Protokoll', shortLabel: 'Protokoll', icon: ScrollText },
];

// HEYKIKI-Orgs: Einstellungen (inkl. Telefon-Settings), Ausgehende Anrufe, Anrufe (eingehend).
// Die 3 Tabs ersetzen das WerkPilot-Layout — der klassische "Telefon"-Tab mit
// internen Sub-Tabs wird für HeyKiki aufgebrochen, damit es nur einen Ort für
// "Einstellungen" gibt.
const HEYKIKI_TABS = [
  { id: 'einstellungen', label: 'Einstellungen', shortLabel: 'Einstell.', icon: Settings },
  { id: 'ausgehende-anrufe', label: 'Ausgehende Anrufe', shortLabel: 'Ausgehend', icon: PhoneOutgoing },
  { id: 'anrufe', label: 'Anrufe', shortLabel: 'Anrufe', icon: Clock }
];

function KiZentrale() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const TABS = isHeykikiOrg ? HEYKIKI_TABS : ALL_TABS;
  const defaultTab = isHeykikiOrg ? 'einstellungen' : 'uebersicht';
  const initialTab = searchParams.get('tab') || defaultTab;
  const [activeTab, setActiveTab] = useState(initialTab);

  const { canView, canEdit, loading } = usePermissions();
  const permissionReadOnly = !canEdit(MODULES.EINSTELLUNGEN);

  // Kiki-Zentrale-Lock: Wenn ein Super-Admin die Org gesperrt hat, ist die
  // gesamte Kiki-Zentrale für den Handwerker schreibgeschützt — der Prompt
  // wird vom HeyKiki-Team manuell gepflegt. Super-Admins, die gerade
  // impersonen, dürfen weiter editieren (Backend lässt ihre PUTs durch).
  const [kikiLocked, setKikiLocked] = useState(false);
  const isImpersonating = typeof window !== 'undefined' && localStorage.getItem('isImpersonating') === 'true';
  useEffect(() => {
    if (loading || !canView(MODULES.EINSTELLUNGEN)) return;
    let cancelled = false;
    (async () => {
      try {
        const settings = await kiZentraleApi.getSettings();
        if (!cancelled) setKikiLocked(!!settings?.manualPromptLocked);
      } catch (e) {
        // Fehler beim Lock-Check ist nicht kritisch — Lock bleibt false,
        // Backend würde im Fall der Fälle bei PUT immer noch 423 senden.
      }
    })();
    return () => { cancelled = true; };
  }, [loading]);

  // Effektiver readOnly = Permission-Check ODER Lock (außer bei Impersonate).
  const readOnly = permissionReadOnly || (kikiLocked && !isImpersonating);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!canView(MODULES.EINSTELLUNGEN)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Bot className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
          <h1 className="text-2xl sm:text-3xl font-serif font-bold">{isHeykikiOrg ? 'KiKi Zentrale' : 'KI Zentrale'}</h1>
          {isHeykikiOrg ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 border border-gray-200">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.8 8.4C14.4 10.2 15.8 11.6 17.6 12.2L24 14L17.6 15.8C15.8 16.4 14.4 17.8 13.8 19.6L12 26L10.2 19.6C9.6 17.8 8.2 16.4 6.4 15.8L0 14L6.4 12.2C8.2 11.6 9.6 10.2 10.2 8.4L12 2Z" transform="scale(0.85) translate(2.1 -1)" fill="#6b7280"/>
              </svg>
              <span className="text-xs font-semibold text-gray-500">Gemini 3</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 border border-gray-200">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" fill="#6b7280"/>
              </svg>
              <span className="text-xs font-semibold text-gray-500">GPT-6</span>
            </span>
          )}
        </div>

        {/* Kiki-Zentrale-Lock-Banner — sichtbar wenn ein Super-Admin die Org
            gesperrt hat. Beim Impersonate wird die Sperre umgangen, dann
            zeigen wir einen abgeschwächten Hinweis statt dem Lock-Banner. */}
        {kikiLocked && !isImpersonating && (
          <div className="mb-6 p-4 rounded-card border border-amber-300 bg-amber-50 flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">KI-Prompt wird {isHeykikiOrg ? 'vom HeyKiki-Team' : 'von unserem Team'} gepflegt</p>
              <p className="text-xs text-amber-800 mt-1">
                Diese {isHeykikiOrg ? 'KiKi-Zentrale' : 'KI-Zentrale'} ist aktuell gesperrt — der Systemprompt deines Telefon-Agenten
                wird manuell durch unser Team konfiguriert. Änderungen an Einstellungen sind
                deaktiviert. Wenn du etwas anpassen möchtest, melde dich gerne bei uns.
              </p>
            </div>
          </div>
        )}
        {kikiLocked && isImpersonating && (
          <div className="mb-6 p-3 rounded-card border border-purple-300 bg-purple-50 flex items-center gap-2">
            <Lock className="w-4 h-4 text-purple-600 flex-shrink-0" />
            <p className="text-xs text-purple-900">
              <strong>Super-Admin-Modus</strong>: Org ist für den Handwerker gesperrt — du darfst trotzdem editieren.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-surface rounded-card shadow-card mb-6 overflow-hidden">
          <nav className="flex overflow-x-auto scrollbar-hide border-b border-border">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.shortLabel}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-4 sm:space-y-6">
          {/* HeyKiki-Layout: 3 Tabs. Unter "Einstellungen" werden die allgemeinen
              KI-Einstellungen (Autonomie-Level, KVA-Versand, Wissensbasis,
              Pflichtfelder, …) + der Telefon-spezifische Block gemeinsam angezeigt,
              damit HeyKiki-Kunden alles an einer Stelle haben. */}
          {isHeykikiOrg ? (
            <>
              {activeTab === 'einstellungen' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Phone className="w-5 h-5 text-primary" />
                      Telefon-Einstellungen
                    </h2>
                    <TelefonAgent readOnly={readOnly} forcedSubTab="einstellungen" hideSubTabs />
                  </div>
                  <div className="border-t border-border pt-6">
                    <KiEinstellungen readOnly={readOnly} />
                  </div>
                </div>
              )}
              {activeTab === 'ausgehende-anrufe' && (
                <TelefonAgent readOnly={readOnly} forcedSubTab="outbound" hideSubTabs />
              )}
              {activeTab === 'anrufe' && (
                <TelefonAgent readOnly={readOnly} forcedSubTab="anrufe" hideSubTabs />
              )}
            </>
          ) : (
            <>
              {activeTab === 'uebersicht' && <KiUebersicht onNavigate={handleTabChange} />}
              {activeTab === 'einstellungen' && <KiEinstellungen readOnly={readOnly} />}
              {activeTab === 'telefon' && <TelefonAgent readOnly={readOnly} />}
              {activeTab === 'whatsapp' && <WhatsAppAgent readOnly={readOnly} />}
              {activeTab === 'email' && <EmailAgent readOnly={readOnly} />}
              {activeTab === 'protokoll' && <KiProtokoll />}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default KiZentrale;
